import React, { useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ArrowBack } from '@material-ui/icons';
import { Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { EmptyState } from '@fluentsms/agentnet-web-components';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import TextDropdown from 'ui-kit/components/dropdowns/TextDropdown';
import ContentContainer from 'ui-kit/components/utility/ContentContainer';
import SectionCard from 'ui-kit/components/card/SectionCard';
import AutopayEmptyIcon from './Icons/AutopayEmptyIcon';
import InvoiceEmptyIcon from './Icons/InvoiceEmptyIcon';
import { useEpayAccounts } from './useEpayAccounts';
import CardBrandIcon from './CardBrandIcon';
import PaymentMethodSummary, { PaymentType } from './PaymentMethodSummary';

const useStyles = makeStyles((theme) =>
  createStyles({
    container: {
      padding: theme.spacing(3),
    },
    backButton: {
      marginBottom: theme.spacing(2),
      alignSelf: 'flex-start',
    },
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: theme.spacing(3),
    },
    pageTitle: {
      fontSize: pxToRem(24),
      fontWeight: 600,
      marginBottom: theme.spacing(0.5),
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(3),
      '& svg': {
        height: 32,
      },
    },
    cardHeaderText: {
      fontSize: pxToRem(14),
      color: theme.palette.text.secondary,
    },
    actions: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    sectionContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(3),
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: theme.spacing(4, 0),
    },
  }),
);

const invoiceTimeframes = [
  { name: 'Last Month', value: 'last_month' },
  { name: 'Last 3 Months', value: 'last_3_months' },
  { name: 'Last Year', value: 'last_year' },
];

const PaymentMethodDetail = () => {
  const classes = useStyles();
  const history = useHistory();
  const { paymentMethodId } = useParams<{ paymentMethodId: string }>();
  const [invoiceTimeframe, setInvoiceTimeframe] = useState('last_month');
  const { accounts, status: paymentMethodStatus } = useEpayAccounts(paymentMethodId);

  const account = useMemo(() => {
    // paymentMethodId is a number from the API but the route param is a string,
    // so compare both as strings to avoid a false "not found".
    return accounts.find((acc) => String(acc.paymentMethodId) === String(paymentMethodId));
  }, [accounts, paymentMethodId]);

  const ePayType = account
    ? account.paymentType === PaymentType.CreditCard
      ? (account.cardBrand as string)
      : account.paymentType
    : '';
  const accountTypeLabel = `${account?.category ?? ''} ${account?.accountType ?? ''}`.trim();

  const pageTitle = account?.nickName || account?.nameOnAccount || 'Payment Method';

  const handleDelete = () => {
    // TODO: Implement delete
  };

  const handleViewPaymentMethod = () => {
    // TODO: Implement view/edit payment method
  };

  if (paymentMethodStatus === 'pending') {
    return (
      <ContentContainer>
        <div className={classes.container}>
          <LoadingSpinner variant="circle" status="pending" />
        </div>
      </ContentContainer>
    );
  }

  if (paymentMethodStatus === 'success' && !account) {
    history.replace('/admin/billing');
    return null;
  }

  return (
    <ContentContainer>
      <div className={classes.container}>
        <AgentNetButton
          variant="text"
          size="small"
          color="primary"
          startIcon={<ArrowBack />}
          onClick={() => history.push('/admin/billing')}
          className={classes.backButton}
          data-testid="payment-method-details-link-back-to-payment-methods"
        >
          Payment Methods
        </AgentNetButton>

        <div className={classes.headerRow}>
          <Typography className={classes.pageTitle}>{pageTitle}</Typography>
          <div className={classes.actions}>
            <AgentNetButton
              variant="contained"
              color="primary"
              size="small"
              onClick={handleViewPaymentMethod}
              data-testid="payment-method-details-button-edit"
            >
              Edit Payment Method
            </AgentNetButton>
            <AgentNetButton
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleDelete}
              data-testid="payment-method-details-button-delete"
            >
              Delete
            </AgentNetButton>
          </div>
        </div>

        <div className={classes.cardHeader}>
          <CardBrandIcon ePayType={ePayType} />
          <Typography className={classes.cardHeaderText}>
            <PaymentMethodSummary
              ePayType={ePayType}
              accountType={accountTypeLabel}
              cardEnding={account?.accountNumber}
              goodThru={account?.expirationDate}
            />
          </Typography>
        </div>

        <div className={classes.sectionContainer}>
          <SectionCard
            title="Autopay Services"
            headerRight={
              <Link to="/admin/autopay-services" style={{ textDecoration: 'none' }}>
                <AgentNetButton
                  variant="text"
                  color="primary"
                  size="small"
                  data-testid="payment-method-details-link-all-autopay"
                >
                  View All Autopay
                </AgentNetButton>
              </Link>
            }
          >
            <EmptyState
              icon={<AutopayEmptyIcon />}
              className={classes.emptyState}
              headerText="No Autopay Services"
              descriptionText="There are currently no autopay services set up for this payment method."
            />
          </SectionCard>

          <SectionCard
            title="Recent Invoices"
            headerRight={
              <TextDropdown
                value={invoiceTimeframe}
                options={invoiceTimeframes}
                onChange={setInvoiceTimeframe}
                data-testid="payment-method-details-button-invoice-filter"
              />
            }
          >
            <EmptyState
              icon={<InvoiceEmptyIcon />}
              className={classes.emptyState}
              headerText="No Recent Invoices"
              descriptionText="There are no invoices for this payment method in the selected timeframe."
            />
          </SectionCard>
        </div>
      </div>
    </ContentContainer>
  );
};

export default PaymentMethodDetail;
